'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import styles from './LanguageDropdown.module.scss'
import Button from '@/components/Button/Button'
import Link from '@/components/Link/Link'

type LanguageDropdownProps = {
  className?: string
  label: string
  dropdownPosition?: 'bottomLeft' | 'bottomRight' | 'topRight' | 'topLeft'
  languageGroups: {
    countryTitle: string
    languages: {
      label: string
      link: SanityLink
      languageCode: string
      isActive?: boolean
    }[]
  }[]
}

const LanguageDropdown = ({
  className,
  dropdownPosition = 'bottomLeft',
  label,
  languageGroups,
}: LanguageDropdownProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasLinkClicked = useRef(false)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  const handleClick = useCallback(() => {
    if (hasLinkClicked.current) return
    hasLinkClicked.current = true
    setTimeout(() => {
      hasLinkClicked.current = false
    }, 50)
    setIsFocused(prev => !prev)
  }, [])

  const handleLinkClick = useCallback(() => {
    // Use setTimeout to defer state update to next tick
    // This prevents state updates during component rendering
    setTimeout(() => {
      setIsFocused(false)
    }, 0)
  }, [])

  const handleLinkFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
    }
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
    }
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false)
    }, 300)
  }, [])

  return (
    <div
      className={classnames(styles.LanguageDropdown, className, { [styles.isFocused]: isFocused })}
      data-dropdown-position={dropdownPosition}
    >
      <Button
        onClick={handleClick}
        onBlur={handleBlur}
        icon={'caretDown'}
        label={label}
        className={styles.button}
      />
      <div
        className={styles.dropdown}
        data-lenis-prevent
      >
        <div className={styles.dropdown__inner}>
          {languageGroups.map((group, groupIndex) => (
            <div key={groupIndex} className={styles.countryGroup}>
              <div className={styles.countryTitle}>{group.countryTitle}</div>
              {group.languages.map((language, langIndex) => (
                <Link
                  key={langIndex}
                  className={classnames(styles.dropdownLink)}
                  link={language.link}
                  language={language.languageCode}
                  onClick={handleLinkClick}
                  onFocus={handleLinkFocus}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

LanguageDropdown.displayName = 'LanguageDropdown'

export default LanguageDropdown
